import React from 'react';
/**
 * Custom React hook to handle pointer-based drag events, supporting drag start, move, end, and cancel logic.
 * Ensures drag logic is only initiated under correct conditions (e.g., left click on a valid tab).
 *
 * @param {PointerDragHandlers} handlers - Object containing event handler callbacks:
 *   - onDragStart: Triggered at drag start.
 *   - onDragMove: Called continuously as the pointer moves.
 *   - onDragEnd: Triggered at drag release.
 *   - onDragCancel: Triggered if drag is cancelled (e.g., ESC, blur, tab close).
 * @param {(event: React.PointerEvent, startPoint?: Point) => Point} mapEventToPoint - Function mapping pointer events to coordinates.
 * @param {(event: React.PointerEvent) => Point} [mapEventToStartPoint] - Optional mapper to extract the starting point from an event.
 *
 * @returns {PointerDragEvents} An object containing pointer event handlers (`onPointerDown`, `onPointerMove`, `onPointerUp`, `onContextMenu`)
 * that can be applied to a draggable UI component.
 */
export function usePointerDrag({ onDragStart, onDragMove, onDragEnd, onDragCancel }, mapEventToPoint, mapEventToStartPoint) {
    const startEventRef = React.useRef(undefined);
    const pointerMovedRef = React.useRef(false);
    React.useEffect(() => {
        const keyDownListener = (event) => {
            if (event.key === 'Escape' && startEventRef.current) {
                onDragCancel();
                startEventRef.current = undefined;
                pointerMovedRef.current = false;
            }
        };
        const blurListener = () => {
            if (startEventRef.current) {
                onDragCancel();
                startEventRef.current = undefined;
                pointerMovedRef.current = false;
            }
        };
        const visibilityChangeListener = () => {
            if (document.visibilityState === 'hidden' && startEventRef.current) {
                onDragCancel();
                startEventRef.current = undefined;
                pointerMovedRef.current = false;
            }
        };
        document.addEventListener('keydown', keyDownListener);
        document.addEventListener('blur', blurListener);
        document.addEventListener('visibilitychange', visibilityChangeListener);
        return () => {
            document.removeEventListener('keydown', keyDownListener);
            document.removeEventListener('blur', blurListener);
            document.removeEventListener('visibilitychange', visibilityChangeListener);
        };
    }, [onDragCancel]);
    return {
        onPointerDown: (event) => {
            var _a;
            if (!event.currentTarget.contains(event.target) ||
                event.target.closest('[data-tab-close-button]')) {
                return;
            }
            const tabPath = (_a = event.currentTarget.closest('[data-tab-path]')) === null || _a === void 0 ? void 0 : _a.dataset.tabPath;
            if (event.button === 0 && tabPath) {
                const point = mapEventToPoint(event);
                if (onDragStart(point, tabPath, event)) {
                    startEventRef.current = mapEventToStartPoint ? mapEventToStartPoint(event) : point;
                    event.stopPropagation();
                    event.preventDefault();
                    event.target.setPointerCapture(event.pointerId);
                }
            }
        },
        onPointerMove: (event) => {
            var _a;
            if (!event.currentTarget.contains(event.target)) {
                return;
            }
            const tabPath = (_a = event.currentTarget.closest('[data-tab-path]')) === null || _a === void 0 ? void 0 : _a.dataset.tabPath;
            if (startEventRef.current && tabPath) {
                onDragMove(mapEventToPoint(event, startEventRef.current), tabPath);
                pointerMovedRef.current = true;
                event.stopPropagation();
                event.preventDefault();
            }
        },
        onPointerUp: (event) => {
            var _a;
            if (!event.currentTarget.contains(event.target)) {
                return;
            }
            const tabPath = (_a = event.currentTarget.closest('[data-tab-path]')) === null || _a === void 0 ? void 0 : _a.dataset.tabPath;
            if (startEventRef.current && tabPath) {
                onDragEnd(mapEventToPoint(event, startEventRef.current), tabPath);
                startEventRef.current = undefined;
                pointerMovedRef.current = false;
                event.stopPropagation();
                event.preventDefault();
            }
        },
        onContextMenu: (event) => {
            if (!event.currentTarget.contains(event.target)) {
                return;
            }
            if (startEventRef.current) {
                onDragCancel();
                startEventRef.current = undefined;
                if (pointerMovedRef.current) {
                    pointerMovedRef.current = false;
                    event.stopPropagation();
                    event.preventDefault();
                }
                else {
                    event.stopPropagation();
                    event.preventDefault();
                }
            }
            else {
                event.stopPropagation();
                event.preventDefault();
            }
        },
    };
}
